// httpPorts — the Resource BC's Material agent-review facet: the pending-
// proposal review queue + accept/reject.

import { Result } from "@bilda/utils";
import type {
    LoadedProposal,
    ProposalPatch,
} from "../../components/Editor/ProposalReview/ProposalReview.js";
import type { ProposalLocation, ProposalPorts } from "../ports/proposal.js";
import type { PortResult } from "../ports/result.js";
import type { MaterialId } from "../ports.js";
import { fields, list, str } from "../wireShape.js";
import { command } from "./shared.js";

// `loadProposals` reads pending agent-edit proposals for a material and parses
// each into the review surface's shape. `acceptProposal`/`rejectProposal` are
// the two distinct commands the domain exposes — accept applies the patch to
// content (the material reloads after), reject discards.

// Parse one wire patch into the UI `ProposalPatch` sum, dropping a shape we
// don't recognise (forward-compat) by returning undefined.
const readPatch = (raw: unknown): ProposalPatch | undefined => {
    const f = fields(raw);
    const type = str(f?.get("type"));
    if (type === "set_element_text") {
        return { type: "set_element_text", text: str(f?.get("text")) ?? "" };
    }
    if (type === "set_element_attrs") {
        const attrs = fields(f?.get("attrs"));
        return {
            type: "set_element_attrs",
            attrs: attrs === undefined ? {} : Object.fromEntries(attrs),
        };
    }
    return undefined;
};

const loadProposals = async (
    materialId: MaterialId,
): PortResult<readonly LoadedProposal[]> => {
    const r = await command("listProposals", { materialId });
    if (Result.isErr(r)) return r;
    const items = list(fields(r.value)?.get("items")).flatMap(
        (raw): readonly LoadedProposal[] => {
            const f = fields(raw);
            const proposalId = str(f?.get("proposalId"));
            const patch = readPatch(f?.get("patch"));
            if (proposalId === undefined || patch === undefined) return [];
            const path = list(f?.get("path"))
                .map((seg) => str(seg))
                .filter((seg): seg is string => seg !== undefined);
            return [
                {
                    proposalId,
                    path,
                    patch,
                    rationale: str(f?.get("rationale")) ?? "",
                },
            ];
        },
    );
    return Result.ok(items);
};

// Locate a proposal by scanning ALL pending proposals (`listProposals` with no
// materialId filter) and matching by id: its material + the edited element
// (the path's last segment). `ok(null)` when the proposal is no longer pending
// (accepted / rejected / unknown), so the caller falls back gracefully.
const locateProposal = async (
    proposalId: string,
): PortResult<ProposalLocation | null> => {
    const r = await command("listProposals", {});
    if (Result.isErr(r)) return r;
    const match = fields(
        list(fields(r.value)?.get("items")).find(
            (raw) => str(fields(raw)?.get("proposalId")) === proposalId,
        ),
    );
    const materialId = str(match?.get("materialId"));
    if (materialId === undefined) return Result.ok(null);
    const elementId = str(list(match?.get("path")).at(-1));
    return Result.ok(
        elementId === undefined ? { materialId } : { materialId, elementId },
    );
};

const acceptProposal = async (proposalId: string): PortResult<void> => {
    const r = await command("acceptProposal", { proposalId });
    return Result.isErr(r) ? r : Result.ok(undefined);
};

const rejectProposal = async (proposalId: string): PortResult<void> => {
    const r = await command("rejectProposal", { proposalId });
    return Result.isErr(r) ? r : Result.ok(undefined);
};

export const proposalHttp: ProposalPorts = {
    loadProposals,
    locateProposal,
    acceptProposal,
    rejectProposal,
};
